import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';

/*
USAGE:

<ProductTreeSelector
    onSelectionChange={(selectedIds) => {
        console.log(selectedIds);
    }}
/>

API RESPONSE FORMAT:

{
    Families: [
        { Id: 1, Name: "fam1" },
    ],
    Products: [
        { Id: 100, Name: "Prod1", FamilyId: 1 },
    ],
}
*/


const ProductTreeSelector = ({
    onSelectionChange,
    legalManufacturerIds = [],
    legalManufacturerNames = [],
    requireLegalManufacturerFilter = false,
}) => {
    const [families, setFamilies] = useState([]);
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [selectedProducts, setSelectedProducts] = useState([]);

    const [productsExpanded, setProductsExpanded] = useState(false);

    const [expandedFamilies, setExpandedFamilies] = useState({});

    const productsCheckboxRef = useRef(null);

    const legalManufacturerIdsKey = Array.isArray(legalManufacturerIds)
        ? legalManufacturerIds.filter(id => id != null && id !== "" && id != -1).join(",")
        : "";

    const hasLegalManufacturerFilter = !!legalManufacturerIdsKey;

    const filterLabel = useMemo(() => {
        const names = (Array.isArray(legalManufacturerNames) ? legalManufacturerNames : [])
            .filter(n => n != null && String(n).trim() !== "");
        if (!names.length && !legalManufacturerIdsKey) return null;
        if (!names.length) {
            return legalManufacturerIdsKey.split(",").length === 1
                ? "filtered by legal manufacturer"
                : "filtered by legal manufacturers";
        }
        if (names.length === 1) {
            return `filtered by legal manufacturer: "${names[0]}"`;
        }
        const quoted = names.map(n => `"${n}"`).join(", ");
        return `filtered by legal manufacturers: ${quoted}`;
    }, [legalManufacturerNames, legalManufacturerIdsKey]);

    useEffect(() => {
        if (requireLegalManufacturerFilter && !hasLegalManufacturerFilter) {
            setFamilies([]);
            setProducts([]);
            setSelectedProducts([]);
            setIsLoading(false);
            return;
        }
        loadData();
    }, [legalManufacturerIdsKey, requireLegalManufacturerFilter]);

    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedProducts);
        }
    }, [selectedProducts]);

    // Drop product selections that are no longer in the filtered list
    useEffect(() => {
        if (!products.length) {
            if (selectedProducts.length) setSelectedProducts([]);
            return;
        }

        const availableIds = new Set(products.map(p => p.Id));
        setSelectedProducts(prev => {
            const next = prev.filter(id => availableIds.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [products]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            let url = "/umbraco/api/RIMSRegistrations/FamiliesAndProducts/";
            const ids = legalManufacturerIdsKey
                ? legalManufacturerIdsKey.split(",").filter(Boolean)
                : [];

            if (ids.length > 0) {
                const params = new URLSearchParams();
                ids.forEach(id => params.append("legalManufacturerIds", id));
                url += `?${params.toString()}`;
            }

            const response = await fetch(url);

            const data = await response.json();

            setFamilies(data.Families || []);
            setProducts(data.Products || []);
        } catch (err) {
            console.error("Failed loading products tree", err);
            setFamilies([]);
            setProducts([]);
        } finally {
            setIsLoading(false);
        }
    };

    const groupedFamilies = useMemo(() => {
        const mappedFamilies = families.map((family) => {
            const familyProducts = products
                .filter((product) => product.FamilyId === family.Id)
                .sort((a, b) => a.Name.localeCompare(b.Name));

            return {
                ...family,
                Products: familyProducts,
            };
        });

        const orphanProducts = products
            .filter(
                (p) =>
                    !p.FamilyId ||
                    !families.some((f) => f.Id === p.FamilyId)
            )
            .sort((a, b) => a.Name.localeCompare(b.Name));

        if (orphanProducts.length > 0) {
            mappedFamilies.push({
                Id: "__no_family__",
                Name: "- Products without family -",
                Products: orphanProducts,
            });
        }

        // SORT families by name
        return mappedFamilies.sort((a, b) =>
            a.Name.localeCompare(b.Name)
        );
    }, [families, products]);

    // -----------------------------
    // Selection Helpers
    // -----------------------------

    const isProductSelected = (productId) => {
        return selectedProducts.includes(productId);
    };

    const isFamilyFullySelected = (family) => {
        if (!family.Products.length) return false;

        return family.Products.every((p) =>
            selectedProducts.includes(p.Id)
        );
    };

    const isFamilyPartiallySelected = (family) => {
        const selectedCount = family.Products.filter((p) =>
            selectedProducts.includes(p.Id)
        ).length;

        return (
            selectedCount > 0 &&
            selectedCount < family.Products.length
        );
    };

    const areAllProductsSelected = () => {
        if (!products.length) return false;

        return products.every((p) =>
            selectedProducts.includes(p.Id)
        );
    };

    const areSomeProductsSelected = () => {
        return selectedProducts.length > 0;
    };

    // -----------------------------
    // Product Selection
    // -----------------------------

    const toggleProduct = (productId) => {
        setSelectedProducts((prev) => {
            if (prev.includes(productId)) {
                return prev.filter((id) => id !== productId);
            }
            return [...prev, productId];
        });
    };

    // -----------------------------
    // Family Selection
    // -----------------------------

    const toggleFamily = (family) => {
        const familyProductIds = family.Products.map((p) => p.Id);

        const fullySelected = familyProductIds.every((id) =>
            selectedProducts.includes(id)
        );

        if (fullySelected) {
            setSelectedProducts((prev) =>
                prev.filter((id) => !familyProductIds.includes(id))
            );
        } else {
            setSelectedProducts((prev) => [
                ...new Set([...prev, ...familyProductIds]),
            ]);
        }
    };

    // -----------------------------
    // Global Selection
    // -----------------------------

    const toggleAllProducts = () => {
        if (areAllProductsSelected()) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(products.map((p) => p.Id));
        }
    };

    // -----------------------------
    // Indeterminate Checkbox States
    // -----------------------------

    useEffect(() => {
        if (productsCheckboxRef.current) {
            productsCheckboxRef.current.indeterminate =
                areSomeProductsSelected() &&
                !areAllProductsSelected();
        }
    }, [selectedProducts]);

    // -----------------------------
    // Expand / Collapse
    // -----------------------------

    const toggleFamilyExpand = (familyId) => {
        setExpandedFamilies((prev) => ({
            ...prev,
            [familyId]: !prev[familyId],
        }));
    };

    if (isLoading) {
        return (
            <div style={{ width: "100%", color: "#666", padding: "4px 0" }}>
                Loading products...
            </div>
        );
    }

    return (
        <div style={{ width: "100%" }}>
            {/* ROOT */}
            <div
                style={{
                    display: "flex",
                    alignItems: "start",
                    gap: "8px",
                    fontWeight: "600",
                }}
            >
                <span
                    style={{
                        cursor: "pointer",
                        minWidth: "16px",
                        display: "inline-block",
                    }}
                    onClick={() =>
                        setProductsExpanded(!productsExpanded)
                    }
                >
                    {productsExpanded ? "▼" : "▶"}
                </span>

                <input
                    ref={productsCheckboxRef}
                    type="checkbox"
                    style={{marginTop: "4px"}}
                    checked={areAllProductsSelected()}
                    onChange={toggleAllProducts}
                />

                <span>📦</span>
                <span>
                    Products
                    {filterLabel ? (
                        <span style={{ fontWeight: "400", color: "#666", marginLeft: "6px" }}>
                            ({filterLabel})
                        </span>
                    ) : null}
                </span>
            </div>

            {/* FAMILIES */}
            {productsExpanded && (
                <div style={{ marginLeft: "24px", marginTop: "8px" }}>
                    {groupedFamilies.map((family) => {
                        const familyCheckboxRef =
                            React.createRef();

                        setTimeout(() => {
                            if (familyCheckboxRef.current) {
                                familyCheckboxRef.current.indeterminate =
                                    isFamilyPartiallySelected(family);
                            }
                        }, 0);

                        return (
                            <div
                                key={family.Id}
                                style={{ marginBottom: "8px" }}
                            >
                                {/* FAMILY ROW */}
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "start",
                                        gap: "8px",
                                    }}
                                >
                                    <span
                                        style={{
                                            cursor: "pointer",
                                            minWidth: "16px",
                                            display: "inline-block",
                                        }}
                                        onClick={() =>
                                            toggleFamilyExpand(family.Id)
                                        }
                                    >
                                        {expandedFamilies[family.Id]
                                            ? "▼"
                                            : "▶"}
                                    </span>

                                    <input
                                        ref={familyCheckboxRef}
                                        type="checkbox"
                                        style={{marginTop: "4px"}}
                                        checked={isFamilyFullySelected(
                                            family
                                        )}
                                        onChange={() =>
                                            toggleFamily(family)
                                        }
                                    />

                                    <span>📁</span>
                                    <span>{family.Name}</span>
                                </div>

                                {/* PRODUCTS */}
                                {expandedFamilies[family.Id] && (
                                    <div
                                        style={{
                                            marginLeft: "32px",
                                            marginTop: "6px",
                                        }}
                                    >
                                        {family.Products.map(
                                            (product) => (
                                                <div
                                                    key={product.Id}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "start",
                                                        gap: "8px",
                                                        marginBottom: "4px",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            minWidth: "16px",
                                                        }}
                                                    />

                                                    <input
                                                        type="checkbox"
                                                        style={{marginTop: "4px"}}
                                                        checked={isProductSelected(
                                                            product.Id
                                                        )}
                                                        onChange={() =>
                                                            toggleProduct(
                                                                product.Id
                                                            )
                                                        }
                                                    />

                                                    <span></span>
                                                    <span>
                                                        {product.SKUCode}&nbsp;&nbsp;•&nbsp;&nbsp;{product.Name}
                                                    </span>
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ProductTreeSelector;